import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Copy, ImageIcon } from "lucide-react";

export default function PhotosTab() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/photos");
      setPhotos(r.data);
    } catch (e) {
      console.error("Failed to load photos:", e);
      toast.error("Failed to load photos");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/admin/photos/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Uploaded ${files.length} photo(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/admin/photos/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copied");
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-[#1A2B4C]">Photo Library</h2>
          <p className="text-sm text-[#6B705C]">
            Upload photos here. Copy the URL and use it in apartments or hero image.
          </p>
        </div>
        <label
          className="inline-flex items-center gap-2 bg-[#1A2B4C] text-white px-5 py-3 rounded-full text-sm uppercase tracking-widest cursor-pointer hover:-translate-y-0.5 transition-transform"
          data-testid="photo-upload-btn"
        >
          <Upload className="w-4 h-4" />
          {uploading ? "Uploading..." : "Upload"}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => upload(Array.from(e.target.files))}
            disabled={uploading}
          />
        </label>
      </div>

      {photos.length === 0 ? (
        <div className="bg-[#F5F2EB] rounded-2xl p-16 text-center border border-dashed border-[#E9E2D3]">
          <ImageIcon className="w-10 h-10 text-[#6B705C] mx-auto mb-4" />
          <p className="text-[#6B705C]">No uploaded photos yet. Click Upload to add your first photo.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p) => (
            <div
              key={p.id}
              data-testid={`photo-${p.id}`}
              className="group relative overflow-hidden rounded-xl bg-[#F5F2EB] border border-[#E9E2D3]"
            >
              <img
                src={p.url}
                alt={p.original_filename}
                className="w-full h-40 object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  onClick={() => copyUrl(p.url)}
                  className="w-9 h-9 rounded-full bg-white text-[#1A2B4C] flex items-center justify-center hover:scale-110 transition-transform"
                  title="Copy URL"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => remove(p.id)}
                  className="w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center hover:scale-110 transition-transform"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-[#6B705C] truncate px-2 py-1.5">{p.original_filename}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
